import React, { useEffect } from 'react';

import { roleNames, useSessionStore, useUserMessageStore } from '../../stores';
import {
  getInvoicePaymentSummary,
  InvoiceListItem,
  InvoicesPaymentCommand,
  InvoicesPaymentSummaryResponse,
  listInvoices,
} from '../../apis/invoiceApi';
import InvoiceListHeader from './components/InvoiceListHeader';
import { sleep } from '../../utils/asyncUtils';
import { UserMessageType } from '../../stores/userMessageStore';
import InvoiceFilteredList from './components/InvoiceFilteredList';

function InvoiceList() {
  const backOfficeRootUrl = useSessionStore.use.backOfficeRootUrl();
  const currentCustomer = useSessionStore.use.currentCustomer();
  const hasRoles = useSessionStore.use.hasRoles();
  const setUserMessage = useUserMessageStore.use.setUserMessage();

  const [isLoading, setIsLoading] = React.useState(false);
  const [rows, setRows] = React.useState<InvoiceListItem[]>([]);

  useEffect(() => {
    setRows([]);
    if (hasRoles(roleNames.billing)) {
      if (backOfficeRootUrl && currentCustomer) {
        setIsLoading(true);
        listInvoices()
          .then((items) => {
            setRows(items);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [currentCustomer, backOfficeRootUrl]);

  const showPaymentResultMessage = (summaryResponse: InvoicesPaymentSummaryResponse) => {
    let messageStatus: UserMessageType = 'success';
    let messageText = summaryResponse.succeededCount === 1 ? '1 invoice was paid' : `${summaryResponse.succeededCount} invoices were paid`;
    if (summaryResponse.failedCount > 0) {
      messageStatus = 'warning';
      messageText += `, ${summaryResponse.failedCount} invoice(s) failed payment`;
    }
    if (summaryResponse.inProgressCount > 0) {
      messageStatus = messageStatus == 'warning' ? 'warning' : 'info';
      messageText += `, ${summaryResponse.inProgressCount} invoice(s) are in process, try refreshing in a few minutes`;
    }
    messageText += '.';

    setUserMessage(messageText, messageStatus, true);
  };

  const pollForInvoicePayments = async ({ paymentMethodId, invoiceIds }: InvoicesPaymentCommand) => {
    setUserMessage('Processing your payments', 'info', true);
    setIsLoading(true);
    let summaryResponse: InvoicesPaymentSummaryResponse = { inProgressCount: 0, succeededCount: 0, failedCount: 0 };
    for (let i = 0; i < 15; i++) {
      await sleep(1000 + i * 100);
      summaryResponse = await getInvoicePaymentSummary({ paymentMethodId, invoiceIds });
      if (summaryResponse.inProgressCount === 0) {
        break;
      }
    }
    showPaymentResultMessage(summaryResponse);
    const invoices = await listInvoices();
    setRows(invoices);
    setIsLoading(false);
  };

  const handlePaymentsSubmitted = async (command: InvoicesPaymentCommand) => {
    if (command.paymentMethodType === 'card') {
      await pollForInvoicePayments(command);
    } else {
      setUserMessage('Processing your payments. Bank payments may take 1-3 days to completed.', 'info', true);
    }
  };

  const handlePaymentMethodAdding = () => {
    setIsLoading(true);
  };

  const handlePaymentMethodAddCompleted = () => {
    setIsLoading(false);
  };

  return (
    <>
      <InvoiceListHeader
        rows={rows}
        onPaymentsSubmitted={handlePaymentsSubmitted}
        isLoading={isLoading}
        onPaymentMethodAdding={handlePaymentMethodAdding}
        onPaymentMethodAddCompleted={handlePaymentMethodAddCompleted}
      />
      <InvoiceFilteredList isLoading={isLoading} rows={rows} backOfficeRootUrl={backOfficeRootUrl} hasBillingRole={hasRoles(roleNames.billing)} />
    </>
  );
}

export default InvoiceList;
